import { useState } from 'react';
import { BrandLogo } from '@/components/brand/Brand';
import { Button } from '@/components/ui/Button';
import { CourseTexture } from '@/components/ui/CourseTexture';
import { Field, PasswordField } from '@/components/ui/Field';
import { ErrorBlock, InsightCard, OfflineBanner, Skeleton } from '@/components/ui/Feedback';
import { Pill, PillGroup, StatusPill, VerifiedPill } from '@/components/ui/Pill';
import { ScoreMark } from '@/components/ui/ScoreMark';
import { SectionBar } from '@/components/ui/SectionBar';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import { Stepper } from '@/components/ui/Stepper';
import type { GeoJSONPoint } from '@/lib/api/types';
import styles from './Showcase.module.css';

/**
 * Living reference for the design system (canvas 1a and 1b). Not part of the product
 * navigation — it exists so every primitive can be checked against the canvas side by
 * side, and so a regression in one is visible without opening ten screens.
 */

// Clube Campestre de Pelotas, the seeded course — a real coordinate, so the satellite
// texture below is a real request and not a mock.
const PELOTAS: GeoJSONPoint = { type: 'Point', coordinates: [-52.4162, -31.7655] };

const NEUTRALS = [
  ['#F7F6F2', 'canvas'],
  ['#FFFFFF', 'surface'],
  ['#EFEDE6', 'sunken'],
  ['#E2DFD6', 'border'],
  ['#6B736D', 'muted'],
  ['#56605A', 'sec.'],
  ['#15201B', 'text'],
] as const;

const GREENS = [
  ['#0C2E1F', 'deep'],
  ['#14472F', 'green'],
  ['#1F6B48', 'mid'],
  ['#2E9463', 'bright'],
  ['#E7EFE9', 'wash'],
] as const;

const GOLDS = [
  ['#7E5F17', 'deep · texto'],
  ['#C1912F', 'gold · gráfico'],
  ['#F0C862', 'bright · s/ ink'],
] as const;

const INKS = [
  ['#0B110D', 'ink'],
  ['#18231C', 'surface'],
  ['#9BA49D', 'muted'],
  ['#F4F3EE', 'on-ink'],
] as const;

const TERRAIN = [
  ['#388E3C', 'green'],
  ['#7CB342', 'fairway'],
  ['#558B2F', 'rough'],
  ['#E2C275', 'bunker'],
  ['#2196F3', 'water'],
  ['#D32F2F', 'ob'],
  ['#B0BEC5', 'tee'],
  ['#1B5E20', 'tree'],
] as const;

function Ramp({
  title,
  colors,
  height = 56,
}: {
  title: string;
  colors: ReadonlyArray<readonly [string, string]>;
  height?: number;
}) {
  return (
    <div className={styles.rampGroup}>
      <div className={styles.cellLabel}>{title}</div>
      <div className={styles.ramp}>
        {colors.map(([hex]) => (
          <div key={hex} className={styles.swatch} style={{ background: hex, height }} />
        ))}
      </div>
      <div className={styles.rampLabels}>
        {colors.map(([hex, label]) => (
          <span key={hex}>{label}</span>
        ))}
      </div>
    </div>
  );
}

export function Showcase() {
  const [strokes, setStrokes] = useState(5);
  const [putts, setPutts] = useState(2);
  const [holes, setHoles] = useState<9 | 18>(18);
  const [password, setPassword] = useState('123456');
  const [insightOpen, setInsightOpen] = useState(true);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <BrandLogo height={64} variant="positive" />
          <p className={styles.eyebrow}>
            Script em verde profundo · bandeira e louro em gold · tagline em maiúsculas espaçadas
          </p>
        </div>
        <p className={styles.headMeta}>
          Design system v1
          <br />
          Editorial · sóbrio · premium
        </p>
      </header>

      {/* ------------------------------------------------------------ paleta */}
      <SectionBar variant="solid">Paleta</SectionBar>
      <section className={styles.panel}>
        <div className={styles.grid}>
          <div className={styles.cell}>
            <Ramp title="Neutros quentes" colors={NEUTRALS} />
            <Ramp title="Marca · verde" colors={GREENS} />
          </div>
          <div className={styles.cell}>
            <Ramp title="Acento · dourado" colors={GOLDS} height={44} />
            <Ramp title="Escuro · visualizador" colors={INKS} height={44} />
          </div>
          <div className={styles.cell}>
            <Ramp title="Terreno · herdado do relógio" colors={TERRAIN} height={36} />
            <p className={styles.goldRule}>
              Regra do dourado: <strong>um por tela</strong>. E <code>gold</code> nunca em corpo de
              texto sobre claro — reprova AA. Texto dourado usa <code>gold-deep</code>.
            </p>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- tipografia */}
      <SectionBar variant="solid">Tipografia</SectionBar>
      <section className={styles.panel}>
        <div className={styles.typeRow}>
          <div className={styles.typeName}>
            Score herói
            <br />
            48→80 · Black
          </div>
          <div className={styles.hero}>84</div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeName}>
            Destaque
            <br />
            30→40 · Bold
          </div>
          <div className={styles.feature}>14,2</div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeName}>
            Título de tela
            <br />
            22→30 · Newsreader
          </div>
          <div className="display" style={{ fontSize: 'var(--type-title)', fontWeight: 500 }}>
            Serra Azul
          </div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeName}>
            Corpo
            <br />
            15→16 · Regular
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>
            Course Rating e Slope ficam congelados no início da volta.
          </div>
        </div>
        <div className={styles.typeRow}>
          <div className={styles.typeName}>
            Tabular
            <br />
            obrigatório
          </div>
          <div className={styles.tnum}>0123456789 · 111 · 000</div>
        </div>
      </section>

      {/* ---------------------------------------------------- notação de score */}
      <SectionBar variant="solid">Notação de score</SectionBar>
      <section className={styles.panel}>
        <div className={styles.scoreRow}>
          {[
            [2, 4, 'EAGLE−'],
            [3, 4, 'BIRDIE'],
            [4, 4, 'PAR'],
            [5, 4, 'BOGEY'],
            [6, 4, 'DOUBLE+'],
          ].map(([s, par, label]) => (
            <div key={label as string} className={styles.scoreCell}>
              <div className={styles.scoreSlot}>
                <ScoreMark strokes={s as number} par={par as number} />
              </div>
              <div className={styles.scoreCellLabel}>{label as string}</div>
            </div>
          ))}
        </div>
        <p className={styles.note}>
          A forma é o canal primário, a cor reforça. Bogey neutro: um bogey não é um alarme.
        </p>
      </section>

      {/* -------------------------------------------------------- componentes */}
      <SectionBar variant="solid">Componentes</SectionBar>
      <section className={styles.panel}>
        <div className={styles.grid}>
          <div className={styles.cell}>
            <div className={styles.cellLabel}>Faixa de seção</div>
            <SectionBar variant="solid" aside="Ver tudo ▸" asideIsAction>
              Scorecard
            </SectionBar>
            <SectionBar aside="variante leve">A volta</SectionBar>
            <p className={styles.note}>Máx. 3 faixas cheias por tela.</p>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Stat block</div>
            <StatRow>
              <StatBlock label="GIR" value="33%" pct={33} caption="6 de 18" />
              <StatBlock label="Putts" value={null} caption="não registrado" />
            </StatRow>
            <p className={styles.note}>Nulo vira “—”. A ausência é informação.</p>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Cápsulas</div>
            <PillGroup wrap>
              <Pill selected={holes === 18} onClick={() => setHoles(18)}>
                18 buracos
              </Pill>
              <Pill selected={holes === 9} onClick={() => setHoles(9)}>
                9 buracos
              </Pill>
            </PillGroup>
            <div className={styles.row} style={{ marginTop: 'var(--space-3)' }}>
              <StatusPill status="completed" />
              <StatusPill status="in_progress" />
              <VerifiedPill />
            </div>
            <div className={styles.row} style={{ marginTop: 'var(--space-3)' }}>
              <Pill dotColor="var(--tee-blue)" caret>
                Tee Azul
              </Pill>
            </div>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Stepper · alvo 44px</div>
            <Stepper label="tacadas" value={strokes} onChange={setStrokes} min={1} max={20} />
            <div className={styles.stack} style={{ marginTop: 'var(--space-4)' }}>
              <div className={styles.row}>
                <Button size="sm">Salvar</Button>
                <Button size="sm" variant="outline">
                  Cancelar
                </Button>
                <Button size="sm" busy>
                  Salvando
                </Button>
              </div>
              <Button variant="danger">Abandonar volta</Button>
            </div>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Stepper compacto</div>
            <Stepper label="putts" value={putts} onChange={setPutts} size="sm" max={10} />
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Cartão de insight</div>
            {insightOpen ? (
              <InsightCard sample="em 12 voltas" onDismiss={() => setInsightOpen(false)}>
                Neste buraco você faz média <strong>+0,8</strong>
              </InsightCard>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setInsightOpen(true)}>
                Restaurar
              </Button>
            )}
            <p className={styles.note}>Um por vez. Sempre com base amostral. Nunca bloqueia.</p>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Campo e erro</div>
            <PasswordField
              label="Senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              showStrength
            />
            <div style={{ marginTop: 'var(--space-4)' }}>
              <ErrorBlock title="Muitas tentativas" secondsLeft={37}>
                Aguarde um minuto antes de tentar de novo.
              </ErrorBlock>
            </div>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Campo com contador</div>
            <Field
              label="Nome de exibição"
              value="Rafael Moreira"
              onChange={() => undefined}
              maxLength={80}
              hint="1 a 80 caracteres"
              showCounter
            />
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Field
                label="Email"
                value="rafael@email"
                onChange={() => undefined}
                error="Email ou senha incorretos"
              />
            </div>
          </div>

          <div className={styles.cell}>
            <div className={styles.cellLabel}>Skeleton</div>
            <Skeleton width="62%" height={11} />
            <Skeleton width="38%" height={34} style={{ marginTop: 12 }} radius={4} />
            <div className={styles.row} style={{ marginTop: 16, flexWrap: 'nowrap' }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ flex: 1 }}>
                  <Skeleton height={8} />
                  <Skeleton height={20} style={{ marginTop: 6 }} />
                </div>
              ))}
            </div>
            <p className={styles.note}>Nunca spinner de tela. Spinner só inline, em botão.</p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- sistema de imagem */}
      <SectionBar variant="solid" aside="canvas 1b">
        Sistema de imagem
      </SectionBar>
      <section className={styles.panel}>
        <div className={styles.textureRow}>
          <div>
            <CourseTexture location={PELOTAS} veil="panel" className={styles.texturePanel} />
            <div className={styles.cellLabel} style={{ marginTop: 12 }}>
              1 · Satélite tratado — interface
            </div>
            <p className={styles.note}>
              Dessaturado e fundido no verde: lê como textura, não como foto. Tile real da ESRI,
              centrado em Pelotas.
            </p>
          </div>
          <div>
            <CourseTexture location={PELOTAS} veil="header" className={styles.textureHeader}>
              <div className="display" style={{ fontSize: 22 }}>
                Clube Campestre
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--on-ink-muted)', marginTop: 4 }}>
                Pelotas · RS — 9 buracos
              </div>
            </CourseTexture>
            <div className={styles.cellLabel} style={{ marginTop: 12 }}>
              2 · Cabeçalho — véu direcional
            </div>
            <p className={styles.note}>O peso visual vem da tipografia, não da imagem.</p>
          </div>
          <div>
            <CourseTexture location={null} className={styles.texturePanel} />
            <div className={styles.cellLabel} style={{ marginTop: 12 }}>
              3 · Verde chapado — fallback
            </div>
            <p className={styles.note}>
              Sem coordenada ou offline. É o estado que precisa existir e não pode parecer erro.
            </p>
          </div>
        </div>
      </section>

      <OfflineBanner />
    </div>
  );
}
